import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import ResponsiveTable from '../common/ResponsiveTable';
import { Column } from '../common/responsiveTable.types';
import SearchableSelect from '../common/SearchableSelect';
import { ProvidentFundService } from '../../services/providentFund.service';
import { IPfProfitPreview, IPfProfitRow, IPfProfitYear } from '../../interfaces/providentFund.interface';
import { useToast } from '../../context/ToastContext';
import { ConfirmDialog, PreviewNotices, SummaryStats, money } from './provisionShared';

interface Props {
    institutionId: string;
    canManage: boolean;
}

// W3-J: annual profit allocation to member balances.
const PfProfitTab: React.FC<Props> = ({ institutionId, canManage }) => {
    const { showSuccess, showApiError } = useToast();
    const thisYear = new Date().getFullYear();
    const [year, setYear] = useState(thisYear - 1);
    const [profit, setProfit] = useState('');
    const [preview, setPreview] = useState<IPfProfitPreview | null>(null);
    const [previewing, setPreviewing] = useState(false);
    const [applying, setApplying] = useState(false);
    const [years, setYears] = useState<IPfProfitYear[]>([]);
    const [loading, setLoading] = useState(false);
    const [reverseTarget, setReverseTarget] = useState<IPfProfitYear | null>(null);
    const [reversing, setReversing] = useState(false);

    const loadYears = useCallback(async () => {
        if (!institutionId) return;
        setLoading(true);
        try {
            setYears(await ProvidentFundService.profitYears(institutionId));
        } catch (e) {
            showApiError(e, 'Failed to load the allocated years');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    useEffect(() => { loadYears(); }, [loadYears]);

    const profitNumber = Number(profit);
    const profitValid = profit.trim() !== '' && profitNumber >= 0.01;
    const yearOptions = Array.from({ length: 10 }, (_, i) => String(thisYear - 8 + i)).map(y => ({ id: y, label: y }));

    const runPreview = async () => {
        setPreviewing(true);
        try {
            setPreview(await ProvidentFundService.profitPreview(institutionId, year, profitNumber));
        } catch (e) {
            setPreview(null);
            showApiError(e, 'Failed to preview the profit allocation');
        } finally {
            setPreviewing(false);
        }
    };

    const apply = async () => {
        setApplying(true);
        try {
            await ProvidentFundService.profitApply(institutionId, { year, distributableProfit: profitNumber });
            showSuccess(`Profit allocation for ${year} applied`);
            setPreview(null);
            await loadYears();
        } catch (e) {
            showApiError(e, 'Failed to apply the profit allocation');
        } finally {
            setApplying(false);
        }
    };

    const reverse = async () => {
        if (!reverseTarget) return;
        setReversing(true);
        try {
            await ProvidentFundService.profitReverse(institutionId, reverseTarget.year);
            showSuccess(`Profit allocation for ${reverseTarget.year} reversed`);
            setReverseTarget(null);
            await loadYears();
        } catch (e) {
            showApiError(e, 'Failed to reverse the allocation');
        } finally {
            setReversing(false);
        }
    };

    const rowColumns: Column<IPfProfitRow>[] = [
        { key: 'emp', header: 'Employee', priority: 1, helpKey: 'payroll.pfProfit.row.col.emp', render: r => r.employeeName },
        { key: 'avg', header: 'Average balance', priority: 2, align: 'right', helpKey: 'payroll.pfProfit.row.col.avg', render: r => money(r.averageBalance) },
        { key: 'amt', header: 'Allocated profit', priority: 1, align: 'right', helpKey: 'payroll.pfProfit.row.col.amt', render: r => money(r.amount) },
    ];

    const yearColumns: Column<IPfProfitYear>[] = [
        { key: 'year', header: 'Year', priority: 1, helpKey: 'payroll.pfProfit.year.col.year', render: y => y.year },
        { key: 'alloc', header: 'Allocated', priority: 1, align: 'right', helpKey: 'payroll.pfProfit.year.col.alloc', render: y => money(y.allocated) },
        { key: 'mem', header: 'Members', priority: 2, align: 'right', helpKey: 'payroll.pfProfit.year.col.mem', render: y => y.members },
        {
            key: 'actions', header: 'Actions', helpKey: 'payroll.pfProfit.year.col.actions', role: 'actions', hideInCard: true,
            render: y => canManage ? <Button size="small" color="error" data-help="payroll.pfProfit.year.action.reverse" onClick={() => setReverseTarget(y)}>Reverse</Button> : null,
        },
    ];

    return (
        <Box data-help-page="payroll.pfProfit.page">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Distributes the year's distributable profit across members in proportion to their average balance.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }} data-help-section="payroll.pfProfit.toolbar">
                <SearchableSelect data-help="payroll.pfProfit.year" label="Year" size="small" options={yearOptions} value={String(year)} clearable={false}
                    onChange={id => { if (id) { setYear(Number(id)); setPreview(null); } }} getOptionLabel={o => o.label}
                    sx={{ flex: '1 1 120px', maxWidth: 160 }} />
                <TextField data-help="payroll.pfProfit.profit" label="Distributable profit" type="number" size="small" value={profit}
                    onChange={e => { setProfit(e.target.value); setPreview(null); }} inputProps={{ min: 0.01, step: '0.01' }}
                    sx={{ flex: '1 1 200px', maxWidth: 260 }} />
                <Button data-help="payroll.pfProfit.preview" variant="outlined" onClick={runPreview} disabled={!institutionId || !profitValid || previewing}>Preview</Button>
                {canManage && (
                    <Button data-help="payroll.pfProfit.apply" variant="contained" onClick={apply} disabled={!preview || !!preview.blocker || applying}>Apply</Button>
                )}
            </Box>

            {previewing && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
            {preview && !previewing && (
                <Box sx={{ mb: 3 }}>
                    <PreviewNotices blocker={preview.blocker} warnings={preview.warnings ?? []} />
                    <SummaryStats items={[
                        { label: 'Distributable profit', value: money(preview.distributableProfit) },
                        { label: 'Allocated', value: money(preview.allocated) },
                    ]} />
                    <ResponsiveTable data-help-section="payroll.pfProfit.previewTable" pageKey="pf-profit-preview" columns={rowColumns} rows={preview.rows}
                        getRowKey={r => r.memberId} emptyMessage="No members to allocate to." />
                </Box>
            )}

            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Allocated years</Typography>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
            ) : (
                <ResponsiveTable data-help-section="payroll.pfProfit.yearsTable" pageKey="pf-profit-years" columns={yearColumns} rows={years} getRowKey={y => String(y.year)}
                    emptyMessage="No profit has been allocated yet." />
            )}

            <ConfirmDialog
                open={!!reverseTarget} title="Reverse profit allocation" confirmLabel="Reverse" busy={reversing}
                message={reverseTarget && `Reverse the profit allocation for ${reverseTarget.year}? The credited profit is removed from every member's balance.`}
                onConfirm={reverse} onClose={() => setReverseTarget(null)}
            />
        </Box>
    );
};

export default PfProfitTab;
